import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { StandardTitle } from './StandardTitle';

const styles = StyleSheet.create({
    container: {
        marginBottom: 20,
    },
    subHeading: {
        fontSize: 14,
        color: '#333',
        fontWeight: 'bold',
        marginTop: 5,
        marginBottom: 10,
    },
    table: {
        display: 'flex',
        width: '100%',
        marginBottom: 20,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        borderRadius: 4,
        overflow: 'hidden',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f0f5ff',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        minHeight: 30,
        alignItems: 'center',
    },
    lastRow: {
        borderBottomWidth: 0,
    },
    tableHeaderCell: {
        padding: 6,
        textAlign: 'center',
        fontSize: 9,
        fontWeight: 'bold',
        color: '#004899',
        flex: 1,
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
    },
    tableCell: {
        padding: 6,
        textAlign: 'center',
        fontSize: 8,
        color: '#333',
        flex: 1,
        borderRightWidth: 1,
        borderRightColor: '#e0e0e0',
    },
    lastCell: {
        borderRightWidth: 0,
    },
    successCell: {
        backgroundColor: '#4caf50',
        color: 'white',
    },
    errorCell: {
        backgroundColor: '#f44336',
        color: 'white',
    },
    noDataText: {
        fontSize: 10,
        color: '#666',
        fontStyle: 'italic',
        padding: 10,
        textAlign: 'center',
    },
    sectionDivider: {
        marginTop: 20,
        marginBottom: 10,
    }
});

interface PreShipSectionProps {
    pallets: Array<any>;
    target?: number;
    parentVariant: string;
}

const POST_STACK_LEAN_ORDER = [
    'Stack start(stacked+30m)',
    'Stack Week-1',
    'Stack Week-2',
    'Stack Week-3',
    'Stack Week-4 (unstacking+30M)',
];

const POST_STACK_HEIGHT_ORDER = [
    'Post stack unit load height (with load still on)',
    'Post stack unit load with Rebound (30 min post load removal)',
];

const orderedGroups = (values: Array<string | null | undefined>, preferredOrder: string[]) => {
    const uniqueValues = Array.from(new Set(values.map(value => value || 'Unspecified')));
    return [
        ...preferredOrder.filter(value => uniqueValues.includes(value)),
        ...uniqueValues.filter(value => !preferredOrder.includes(value)).sort(),
    ];
};

export const PreShipSection: React.FC<PreShipSectionProps> = ({ pallets,parentVariant, target = 2 }) => {
    // Calculate lean value from top and bottom measurements
    const calculateLean = (top: string, bottom: string): number => {
        const topVal = parseFloat(top);
        const bottomVal = parseFloat(bottom);
        if (isNaN(topVal) || isNaN(bottomVal)) return 0;
        return Math.abs(topVal - bottomVal);
    };

    // Check if a lean value exceeds the target
    const exceedsTarget = (value: number): boolean => {
        return value > target;
    };

    // Function to get style for a cell based on its value
    const getCellStyle = (top: string, bottom: string): object => {
        const leanValue = calculateLean(top, bottom);
        return exceedsTarget(leanValue) ? styles.errorCell : styles.successCell;
    };

    // Format the lean value for display
    const formatLeanValue = (top: string, bottom: string): string => {
        const leanValue = calculateLean(top, bottom);
        return leanValue.toFixed(2);
    };

    const postStackLeanGroups = parentVariant === 'postStack'
        ? orderedGroups(
            pallets.flatMap(pallet => (pallet.lpnNumbers || []).flatMap((lpn: any) =>
                (lpn.leans || [])
                    .filter((lean: any) => lean.variant === parentVariant)
                    .map((lean: any) => lean.weekType)
            )),
            POST_STACK_LEAN_ORDER,
        )
        : [];

    const postStackHeightGroups = parentVariant === 'postStack'
        ? orderedGroups(
            pallets.flatMap(pallet => (pallet.lpnNumbers || []).flatMap((lpn: any) =>
                (lpn.unitLoadHeights || [])
                    .filter((height: any) => height.variant === parentVariant)
                    .map((height: any) => height.weekType)
            )),
            POST_STACK_HEIGHT_ORDER,
        )
        : [];

    const renderLeanTable = (weekType?: string) => {
        const rows = pallets.flatMap((pallet, palletIndex) =>
            (pallet.lpnNumbers || []).flatMap((lpn: any, lpnIndex: number) =>
                (lpn.leans || [])
                    .filter((lean: any) => (
                        lean.variant === parentVariant &&
                        (weekType === undefined || (lean.weekType || 'Unspecified') === weekType)
                    ))
                    .map((lean: any, leanIndex: number) => ({ pallet, palletIndex, lpn, lpnIndex, lean, leanIndex }))
            )
        );

        return (
            <View style={styles.table}>
                <View style={styles.tableHeader} wrap={false}>
                    <Text style={styles.tableHeaderCell}>Pallet Name</Text>
                    <Text style={styles.tableHeaderCell}>Pallet Variant</Text>
                    <Text style={styles.tableHeaderCell}>LPN Number</Text>
                    <Text style={styles.tableHeaderCell}>End Status</Text>
                    <Text style={styles.tableHeaderCell}>Side A (in)</Text>
                    <Text style={styles.tableHeaderCell}>Side B (in)</Text>
                    <Text style={styles.tableHeaderCell}>Side C (in)</Text>
                    <Text style={[styles.tableHeaderCell, styles.lastCell]}>Side D (in)</Text>
                </View>
                {rows.map(({ pallet, palletIndex, lpn, lpnIndex, lean, leanIndex }, rowIndex) => (
                    <View
                        key={`${parentVariant}-lean-${palletIndex}-${lpnIndex}-${leanIndex}`}
                        style={[styles.tableRow, rowIndex === rows.length - 1 ? styles.lastRow : {}]}
                        wrap={false}
                    >
                        <Text style={styles.tableCell}>{pallet.name || 'N/A'}</Text>
                        <Text style={styles.tableCell}>{pallet.variant || 'N/A'}</Text>
                        <Text style={styles.tableCell}>{lpn.lpnNo || 'N/A'}</Text>
                        <Text style={styles.tableCell}>{parentVariant === 'postStack' ? lean.weekType || 'N/A' : lpn.endStatus || 'N/A'}</Text>
                        <Text style={[styles.tableCell, getCellStyle(lean.sideATop, lean.sideABottom)]}>
                            {formatLeanValue(lean.sideATop, lean.sideABottom)}
                        </Text>
                        <Text style={[styles.tableCell, getCellStyle(lean.sideBTop, lean.sideBBottom)]}>
                            {formatLeanValue(lean.sideBTop, lean.sideBBottom)}
                        </Text>
                        <Text style={[styles.tableCell, getCellStyle(lean.sideCTop, lean.sideCBottom)]}>
                            {formatLeanValue(lean.sideCTop, lean.sideCBottom)}
                        </Text>
                        <Text style={[styles.tableCell, styles.lastCell, getCellStyle(lean.sideDTop, lean.sideDBottom)]}>
                            {formatLeanValue(lean.sideDTop, lean.sideDBottom)}
                        </Text>
                    </View>
                ))}
            </View>
        );
    };

    const renderHeightTable = (weekType?: string) => {
        const rows = pallets.flatMap((pallet, palletIndex) =>
            (pallet.lpnNumbers || []).flatMap((lpn: any, lpnIndex: number) =>
                (lpn.unitLoadHeights || [])
                    .filter((height: any) => (
                        height.variant === parentVariant &&
                        (weekType === undefined || (height.weekType || 'Unspecified') === weekType)
                    ))
                    .map((height: any, heightIndex: number) => ({ pallet, palletIndex, lpn, lpnIndex, height, heightIndex }))
            )
        );

        return (
            <View style={styles.table}>
                <View style={styles.tableHeader} wrap={false}>
                    <Text style={styles.tableHeaderCell}>Pallet Name</Text>
                    <Text style={styles.tableHeaderCell}>Pallet Variant</Text>
                    <Text style={styles.tableHeaderCell}>LPN Number</Text>
                    <Text style={styles.tableHeaderCell}>A-B (in)</Text>
                    <Text style={styles.tableHeaderCell}>B-C (in)</Text>
                    <Text style={styles.tableHeaderCell}>C-D (in)</Text>
                    <Text style={[styles.tableHeaderCell, styles.lastCell]}>D-A (in)</Text>
                </View>
                {rows.map(({ pallet, palletIndex, lpn, lpnIndex, height, heightIndex }, rowIndex) => (
                    <View
                        key={`${parentVariant}-height-${palletIndex}-${lpnIndex}-${heightIndex}`}
                        style={[styles.tableRow, rowIndex === rows.length - 1 ? styles.lastRow : {}]}
                        wrap={false}
                    >
                        <Text style={styles.tableCell}>{pallet.name || 'N/A'}</Text>
                        <Text style={styles.tableCell}>{pallet.variant || 'N/A'}</Text>
                        <Text style={styles.tableCell}>{lpn.lpnNo || 'N/A'}</Text>
                        <Text style={styles.tableCell}>{height.heightAB || 'N/A'}</Text>
                        <Text style={styles.tableCell}>{height.heightBC || 'N/A'}</Text>
                        <Text style={styles.tableCell}>{height.heightCD || 'N/A'}</Text>
                        <Text style={[styles.tableCell, styles.lastCell]}>{height.heightDA || 'N/A'}</Text>
                    </View>
                ))}
            </View>
        );
    };

    // Check if any pallets have preship lean data
    const hasPreshipLeanData = pallets.some(pallet =>
        pallet.lpnNumbers &&
        pallet.lpnNumbers.some((lpn: any) =>
            lpn.leans &&
            lpn.leans.some((lean: any) => lean.variant === parentVariant)
        )
    );

    // Check if any pallets have preship FTL data
    const hasPreshipFtlData = pallets.some(pallet =>
        pallet.lpnNumbers &&
        pallet.lpnNumbers.some((lpn: any) =>
            lpn.ftlRows &&
            lpn.ftlRows.some((ftl: any) => ftl.variant === parentVariant)
        )
    );

    // Check if any pallets have preship unit load position data
    const hasPreshipPositionData = pallets.some(pallet =>
        pallet.lpnNumbers &&
        pallet.lpnNumbers.some((lpn: any) =>
            lpn.unitLoadPositions &&
            lpn.unitLoadPositions.some((pos: any) => pos.variant === parentVariant)
        )
    );

    // Check if any pallets have preship unit load height data
    const hasPreshipHeightData = pallets.some(pallet =>
        pallet.lpnNumbers &&
        pallet.lpnNumbers.some((lpn: any) =>
            lpn.unitLoadHeights &&
            lpn.unitLoadHeights.some((height: any) => height.variant === parentVariant)
        )
    );

    // If no preship data at all, show message
    if (!hasPreshipLeanData && !hasPreshipFtlData && !hasPreshipPositionData && !hasPreshipHeightData) {
        return (
            <View style={styles.container}>
                <Text style={styles.noDataText}>No {parentVariant} data available</Text>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {/* Preship Lean Section */}
            {hasPreshipLeanData && (
                <>
                    <StandardTitle title={parentVariant === 'postStack' ? 'Post-Stack Lean' : `${parentVariant} Lean`} />
                    {parentVariant === 'postStack'
                        ? postStackLeanGroups.map(weekType => (
                            <View key={weekType} style={styles.sectionDivider}>
                                <Text style={styles.subHeading} minPresenceAhead={60}>{weekType}</Text>
                                {renderLeanTable(weekType)}
                            </View>
                        ))
                        : renderLeanTable()}
                </>
            )}

            {/* Preship FTL Section */}
            {hasPreshipFtlData && (
                <View style={styles.sectionDivider}>
                    <Text style={styles.subHeading}>{parentVariant} FTL</Text>
                    <View style={styles.table}>
                        <View style={styles.tableHeader} wrap={false}>
                            <Text style={styles.tableHeaderCell}>Pallet Name</Text>
                            <Text style={styles.tableHeaderCell}>Pallet Variant</Text>
                            <Text style={styles.tableHeaderCell}>LPN Number</Text>
                            <Text style={styles.tableHeaderCell}>Top Measured (lbs)</Text>
                            <Text style={styles.tableHeaderCell}>Middle Measured (lbs)</Text>
                            <Text style={[styles.tableHeaderCell, styles.lastCell]}>Bottom Measured (lbs)</Text>
                        </View>

                        {pallets.map((pallet, palletIndex) => (
                            pallet.lpnNumbers && pallet.lpnNumbers.map((lpn: any, lpnIndex: number) => (
                                lpn.ftlRows && lpn.ftlRows
                                    .filter((ftl: any) => ftl.variant === parentVariant)
                                    .map((ftl: any, ftlIndex: number) => (
                                        <View
                                            key={`${parentVariant}-ftl-${palletIndex}-${lpnIndex}-${ftlIndex}`}
                                            style={[
                                                styles.tableRow,
                                                ftlIndex === lpn.ftlRows.filter((f: any) => f.variant === parentVariant).length - 1 &&
                                                    lpnIndex === pallet.lpnNumbers.length - 1 &&
                                                    palletIndex === pallets.length - 1 ? styles.lastRow : {}
                                            ]}
                                            wrap={false}
                                        >
                                            <Text style={styles.tableCell}>{pallet.name || 'N/A'}</Text>
                                            <Text style={styles.tableCell}>{pallet.variant || 'N/A'}</Text>
                                            <Text style={styles.tableCell}>{lpn.lpnNo || 'N/A'}</Text>
                                            <Text style={styles.tableCell}>{ftl.topMeasured || 'N/A'}</Text>
                                            <Text style={styles.tableCell}>{ftl.middleMeasured || 'N/A'}</Text>
                                            <Text style={[styles.tableCell, styles.lastCell]}>{ftl.bottomMeasured || 'N/A'}</Text>
                                        </View>
                                    ))
                            ))
                        ))}
                    </View>
                </View>
            )}

            {/* Preship Unit Load Position Section */}
            {hasPreshipPositionData && (
                <View style={styles.sectionDivider}>
                    <Text style={styles.subHeading}>{parentVariant} Unit Load Position</Text>
                    <View style={styles.table}>
                        <View style={styles.tableHeader} wrap={false}>
                            <Text style={styles.tableHeaderCell}>Pallet Name</Text>
                            <Text style={styles.tableHeaderCell}>Pallet Variant</Text>
                            <Text style={styles.tableHeaderCell}>LPN Number</Text>
                            <Text style={styles.tableHeaderCell}>Side A Position (in)</Text>
                            <Text style={styles.tableHeaderCell}>Side B Position (in)</Text>
                            <Text style={styles.tableHeaderCell}>Side C Position (in)</Text>
                            <Text style={[styles.tableHeaderCell, styles.lastCell]}>Side D Position (in)</Text>
                        </View>

                        {pallets.map((pallet, palletIndex) => (
                            pallet.lpnNumbers && pallet.lpnNumbers.map((lpn: any, lpnIndex: number) => (
                                lpn.unitLoadPositions && lpn.unitLoadPositions
                                    .filter((pos: any) => pos.variant === parentVariant)
                                    .map((pos: any, posIndex: number) => (
                                        <View
                                            key={`${parentVariant}-position-${palletIndex}-${lpnIndex}-${posIndex}`}
                                            style={[
                                                styles.tableRow,
                                                posIndex === lpn.unitLoadPositions.filter((p: any) => p.variant === parentVariant).length - 1 &&
                                                    lpnIndex === pallet.lpnNumbers.length - 1 &&
                                                    palletIndex === pallets.length - 1 ? styles.lastRow : {}
                                            ]}
                                            wrap={false}
                                        >
                                            <Text style={styles.tableCell}>{pallet.name || 'N/A'}</Text>
                                            <Text style={styles.tableCell}>{pallet.variant || 'N/A'}</Text>
                                            <Text style={styles.tableCell}>{lpn.lpnNo || 'N/A'}</Text>
                                                <Text style={styles.tableCell}>{pos.sideAPosition || 'N/A'}</Text>
                                            <Text style={styles.tableCell}>{pos.sideBPosition || 'N/A'}</Text>
                                            <Text style={styles.tableCell}>{pos.sideCPosition || 'N/A'}</Text>
                                            <Text style={[styles.tableCell, styles.lastCell]}>{pos.sideDPosition || 'N/A'}</Text>
                                        </View>
                                    ))
                            ))
                        ))}
                    </View>
                </View>
            )}

            {/* Preship Unit Load Height Section */}
            {hasPreshipHeightData && (
                <View style={styles.sectionDivider}>
                    <Text style={styles.subHeading}>{parentVariant === 'postStack' ? 'Post-Stack Unit Load Height' : `${parentVariant} Unit Load Height`}</Text>
                    {parentVariant === 'postStack'
                        ? postStackHeightGroups.map(weekType => (
                            <View key={weekType}>
                                <Text style={styles.subHeading} minPresenceAhead={60}>{weekType}</Text>
                                {renderHeightTable(weekType)}
                            </View>
                        ))
                        : renderHeightTable()}
                </View>
            )}
        </View>
    );
};
